import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Input,
  Select,
  Card,
  Badge,
  Pagination,
  Modal,
  ModalFooter,
  EntityLink,
  InsurerLogo,
  CompactFilterBar,
} from '@/components/common';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { PolicyNumber, policyNumberLabel } from '@/components/common/PolicyNumber';
import type { SortDirection } from '@/components/common/Table';
import { useServicingRequests, useCreateServicingRequest } from '@/hooks/useRenewals';
import { usePolicies } from '@/hooks/usePolicies';
import { policyService } from '@/services/policies';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useFilterParams } from '@/hooks/useFilterParams';
import { useAuthStore } from '@/store/auth';
import { useCan } from '@/hooks/useEmployeeRights';
import { cn, formatDateTime } from '@/utils';
import type { ServicingRequestFilters } from '@/types';
import {
  REQUEST_PRIORITIES,
  REQUEST_STATUSES,
  REQUEST_TYPES,
  priorityBadgeVariant,
  isRequestOverdue,
  priorityLabel,
  requestStatusBadgeVariant,
  requestTypeLabel,
  statusLabel,
} from './servicingRequestOptions';

// B2: these three lists used to be invented here and matched nothing on
// `ServiceRequest` -- see servicingRequestOptions.ts. They now come from the
// single mirror of the model's choices.
const statusOptions = [{ value: '', label: 'All Status' }, ...REQUEST_STATUSES];
const typeOptions = [{ value: '', label: 'All Types' }, ...REQUEST_TYPES];
const priorityOptions = [{ value: '', label: 'All Priority' }, ...REQUEST_PRIORITIES];

/** The values each control can legitimately hold. A restored `?f_status=` (or
 *  `?f_priority=`, `?f_request_type=`) outside its set is DROPPED rather than
 *  sent: all three are `filterset_fields` on `ServiceRequestViewSet`, and
 *  django-filter answers an unknown choice with a 400 -- so a hand-edited or
 *  stale URL would take the whole list down instead of being ignored. */
const STATUS_VALUES = new Set(REQUEST_STATUSES.map((option) => option.value));
const TYPE_VALUES = new Set(REQUEST_TYPES.map((option) => option.value));
const PRIORITY_VALUES = new Set(REQUEST_PRIORITIES.map((option) => option.value));

/**
 * Q-01 -- the filter state this page mirrors into the query string.
 *
 * DELIBERATELY NOT HERE: the page number. Every filter change resets it to 1,
 * so a restored number would be clobbered before the first paint.
 */
type ServicingPanelFilters = {
  search: string;
  status: string;
  request_type: string;
  priority: string;
  /** U-04: the server ordering field, '' while the list is in its default order. */
  sort: string;
  sort_dir: string;
};

const EMPTY_FILTERS: ServicingPanelFilters = {
  search: '',
  status: '',
  request_type: '',
  priority: '',
  sort: '',
  sort_dir: '',
};

/**
 * Namespaced even though nothing links to `/servicing-requests?…` today, so
 * that adding a deep link later cannot collide with a value the user chose.
 * There is therefore no `initial` seed and no `consumeSeedParams` here:
 * `clearAll()` empties everything this page owns, so Clear genuinely survives
 * a refresh.
 */
const FILTER_PREFIX = 'f_';

/**
 * U-04: the ONLY fields `ServiceRequestViewSet.ordering_fields` accepts
 * (backend/apps/renewals/views.py) -- `["created_at", "sla_due_date"]`.
 *
 * Of the seven columns here, exactly one is in it: Created. `sla_due_date` is
 * orderable but is not displayed, and the other six (request number, policy,
 * type, priority, status, assignee) are NOT orderable server-side. DRF's
 * OrderingFilter silently DROPS a term it does not recognise and returns the
 * default order, so marking any of those sortable would ship a header that
 * fires a request and changes nothing.
 */
const SORTABLE_FIELDS = new Set(['created_at', 'sla_due_date']);

/**
 * U-02: the column widths.
 *
 * Applied through a `<colgroup>` under `table-fixed`, so the columns stop
 * being re-derived from whichever rows a filter happens to leave on screen
 * (G2's "columns shift when filters are applied").
 *
 * `w-full` IS NOT REDUNDANT beside `min-w-full`: `table-layout: fixed` is
 * INERT while the table's used width is auto (CSS 2.1 17.5.2.1 only applies
 * the fixed algorithm when `width` computes to something other than `auto`),
 * and `min-w-full` sets a MINIMUM, not a width. Without it the browser falls
 * straight back to the auto algorithm and every declared width below is a
 * no-op that still reads as shipped.
 *
 * SIZE = CONTENT + CELL PADDING. Cells are border-box and truncate, so the
 * space for content is the width minus that column's own horizontal padding:
 *
 *   request    14rem  px-6 -> 176px  (a number over the subject line)
 *   policy     12rem  px-6 -> 144px
 *   type        9rem  px-3 -> 120px  ("General Inquiry" badge: ~110px)
 *   priority  7.5rem  px-3 ->  96px
 *   status      9rem  px-3 -> 120px  ("Pending Documents": ~118px)
 *   assignee   10rem  px-6 -> 112px
 *   created    11rem  px-3 -> 152px  ("Jul 20, 2026, 09:00 am": ~140px)
 *             -------
 *              72.5rem = 1160px, inside the ~1424px a 1440-1536px desktop can
 *   show with no horizontal page scrollbar. Narrower than that the TABLE
 *   scrolls sideways inside its own card, not the page.
 */
const COLUMN_WIDTHS = ['14rem', '12rem', '9rem', '7.5rem', '9rem', '10rem', '11rem'];

/** The padding each column overrides `px-6` with, `null` to keep it. */
const TIGHT = 'px-3';

type RequesterField = 'requester_name' | 'requester_email' | 'requester_phone';

export function ServicingRequestsPage() {
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  // C4/C6: `requester_name`, `requester_email` and `requester_phone` are
  // declared on the ServiceRequest model WITHOUT blank=True, so DRF makes all
  // three required. This form never sent them, so every submission failed with
  // three "This field is required." errors -- that is the reported validation
  // error, and adding the fields is the whole fix. No backend change.
  // B2: `request_type` defaulted to 'query', which is not one of
  // ServiceRequest.REQUEST_TYPE_CHOICES -- every submission left on the default
  // came back 400 "is not a valid choice". 'general_inquiry' is the real code.
  const [newRequest, setNewRequest] = useState({
    policy: '',
    request_type: 'general_inquiry',
    subject: '',
    description: '',
    priority: 'medium',
    requester_name: '',
    requester_email: '',
    requester_phone: '',
  });

  // DQ #376: requester fields the user has typed into since the last policy
  // selection. A policy's holder details fill only the fields NOT in this set.
  const touchedRequester = useRef<Set<RequesterField>>(new Set());
  // Guards the async policy-detail fetch against a later selection.
  const prefillToken = useRef(0);
  const editRequester = (field: RequesterField, value: string) => {
    touchedRequester.current.add(field);
    setNewRequest((prev) => ({ ...prev, [field]: value }));
  };

  const navigate = useNavigate();
  const { user } = useAuthStore();
  // B4: raising a request is servicing Create; `useCan` passes customers through.
  const canCreateRequest = useCan('servicing', 'create');

  // Q-01: the panel's own filters and the sort, mirrored into `?f_*` so they
  // survive a refresh, a bookmark and a shared link. Restored SYNCHRONOUSLY in
  // the hook's lazy initializer, so the first request is already filtered and
  // the filter panel is already open over it.
  const { filters, setFilters, clearAll } = useFilterParams<ServicingPanelFilters>({
    defaults: EMPTY_FILTERS,
    prefix: FILTER_PREFIX,
  });

  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `searchTerm` drives the Input synchronously, `debouncedTerm` (300ms) feeds
  // the filters. Seeded from `?f_search=` so a refresh brings the box back
  // filled in, not just the results.
  const {
    term: searchTerm,
    debouncedTerm,
    setTerm: setSearchTerm,
  } = useDebouncedSearch(filters.search);

  const status = STATUS_VALUES.has(filters.status) ? filters.status : '';
  const requestType = TYPE_VALUES.has(filters.request_type) ? filters.request_type : '';
  const priority = PRIORITY_VALUES.has(filters.priority) ? filters.priority : '';
  // U-04: only a field the server can order by, so a stale or hand-edited
  // `?f_sort=` cannot leave a header drawn as sorted over a list that is not.
  const sortKey = SORTABLE_FIELDS.has(filters.sort) ? filters.sort : '';
  const sortDirection: SortDirection = filters.sort_dir === 'desc' ? 'desc' : 'asc';
  const ordering = sortKey ? `${sortDirection === 'desc' ? '-' : ''}${sortKey}` : undefined;

  const queryFilters = useMemo<ServicingRequestFilters>(
    () => ({
      search: filters.search || undefined,
      status: status || undefined,
      request_type: requestType || undefined,
      priority: priority || undefined,
      ordering,
    }),
    [filters.search, status, requestType, priority, ordering]
  );

  const { data, isLoading, isError, refetch } = useServicingRequests(page, queryFilters);
  const createMutation = useCreateServicingRequest();

  // C5: the policy was a free-text box asking the customer to paste a raw
  // UUID -- a second, independent source of the same validation failure. Load
  // the caller's own policies for the dropdown. The server-side ownership
  // check in perform_create is unchanged and remains the security boundary;
  // this is a usability fix.
  // DQ #367: the dropdown only ever showed page 1 (20 records), so a policy
  // outside it could not be found. Typing now searches the server (debounced);
  // an empty box keeps the first page.
  const {
    term: policyQuery,
    debouncedTerm: debouncedPolicyQuery,
    setTerm: setPolicyQuery,
  } = useDebouncedSearch('');
  // An emptied box (clear, close, reopen after create) drops the filter at once
  // rather than waiting out the debounce for the stale term.
  const policyFilters = useMemo(() => {
    const search = policyQuery.trim() ? debouncedPolicyQuery.trim() : '';
    return search ? { search } : undefined;
  }, [policyQuery, debouncedPolicyQuery]);
  const { data: policiesData, isFetching: policiesFetching } = usePolicies(1, policyFilters, {
    enabled: showCreateModal,
  });
  const [selectedPolicyOption, setSelectedPolicyOption] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const policyOptions = useMemo(() => {
    const opts = (policiesData?.results ?? []).map((p) => ({
      value: p.id,
      label: [policyNumberLabel(p), p.insurer_name, p.holder_name].filter(Boolean).join(' — '),
    }));
    // Keep the chosen policy visible even when a later search no longer returns it.
    if (selectedPolicyOption && !opts.some((o) => o.value === selectedPolicyOption.value)) {
      opts.unshift(selectedPolicyOption);
    }
    return opts;
  }, [policiesData, selectedPolicyOption]);

  // Prefill the requester from the logged-in user, but leave the fields
  // editable: a customer may legitimately raise a request on behalf of a
  // family member covered by the same policy.
  useEffect(() => {
    if (!showCreateModal || !user) return;
    setNewRequest((prev) => ({
      ...prev,
      requester_name: prev.requester_name || user.full_name || '',
      requester_email: prev.requester_email || user.email || '',
      requester_phone: prev.requester_phone || user.phone || '',
    }));
  }, [showCreateModal, user]);

  const requests = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const itemsPerPage = 20;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Push the debounced term into the filters and reset to page 1. Keyed off a
  // ref of the LAST TERM PUSHED, not off `filters.search`: seeded with the
  // restored value the mount pass is a no-op, and Clear is not undone by the
  // 300ms of stale `debouncedTerm` still in flight.
  const pushedTermRef = useRef(filters.search);
  useEffect(() => {
    if (pushedTermRef.current === debouncedTerm) return;
    pushedTermRef.current = debouncedTerm;
    setFilters({ search: debouncedTerm });
    setPage(1);
  }, [debouncedTerm, setFilters]);

  const handleFilterChange = (key: 'status' | 'request_type' | 'priority', value: string) => {
    setFilters({ [key]: value } as Partial<ServicingPanelFilters>);
    setPage(1);
  };

  // U-04: server-side sort. Nothing here reorders the rows in hand -- this
  // sends DRF's `?ordering=`, so the order is the whole filtered queue's, not
  // this page of twenty.
  const handleSortChange = (key: string, direction: SortDirection) => {
    setFilters({ sort: key, sort_dir: direction });
    setPage(1);
  };

  // Only the PANEL's own three controls. The search box is always visible, and
  // the SORT is a view preference rather than a filter -- it narrows nothing,
  // so it must not badge.
  const activeFilterCount = [status, requestType, priority].filter(Boolean).length;

  const clearFilters = () => {
    setSearchTerm('');
    // Also resets the sort, which lives in the same namespace so a shared link
    // reproduces the view the sender was looking at.
    clearAll();
    setPage(1);
  };

  const canSubmit =
    !!newRequest.policy &&
    !!newRequest.subject &&
    !!newRequest.description &&
    !!newRequest.requester_name &&
    !!newRequest.requester_email &&
    !!newRequest.requester_phone;

  // Cancel/close keeps the whole draft, including the selected policy and its
  // pinned option (which labels it); only the search box resets, so a reopened
  // modal shows the first page. A successful create clears the draft instead.
  const closeCreateModal = () => {
    setShowCreateModal(false);
    setPolicyQuery('');
  };

  // DQ #376: a broker/admin raises a request for a customer's policy, so the
  // requester is that policy's holder, not the logged-in user. The list row has
  // name and phone; only the detail serializer carries the email.
  const handlePolicyHolderPrefill = (policyId: string) => {
    touchedRequester.current = new Set();
    const token = ++prefillToken.current;
    if (!policyId) return;
    const fill = (holder: {
      holder_name?: string | null;
      holder_phone?: string | null;
      holder_email?: string | null;
    }) => {
      const values: Record<RequesterField, string | null | undefined> = {
        requester_name: holder.holder_name,
        requester_email: holder.holder_email,
        requester_phone: holder.holder_phone,
      };
      setNewRequest((prev) => {
        const next = { ...prev };
        (Object.keys(values) as RequesterField[]).forEach((field) => {
          const v = values[field];
          if (v && !touchedRequester.current.has(field)) next[field] = v;
        });
        return next;
      });
    };
    const row = policiesData?.results.find((p) => p.id === policyId);
    if (row) fill(row);
    policyService
      .getPolicy(policyId)
      .then((detail) => {
        if (token === prefillToken.current) fill(detail);
      })
      .catch(() => {
        // Name/phone from the list row stay; email is simply left as it was.
      });
  };

  const handleCreate = () => {
    if (canSubmit) {
      createMutation.mutate(newRequest as Parameters<typeof createMutation.mutate>[0], {
        onSuccess: () => {
          touchedRequester.current = new Set();
          prefillToken.current += 1;
          setShowCreateModal(false);
          setSelectedPolicyOption(null);
          setPolicyQuery('');
          setNewRequest({
            policy: '',
            request_type: 'general_inquiry',
            subject: '',
            description: '',
            priority: 'medium',
            requester_name: '',
            requester_email: '',
            requester_phone: '',
          });
        },
      });
    }
  };

  return (
    <div>
      <PageHeader
        title="Servicing Requests"
        subtitle={`${totalItems} requests`}
        // Servicing Requests is its own top-level section (its own route, its
        // own sidebar entry). The Renewals ancestor here was the last thing
        // still presenting it as a sub-item of Renewals.
        breadcrumbs={[{ label: 'Servicing Requests' }]}
        actions={
          canCreateRequest ? (
            <Button
              leftIcon={<PlusIcon className="h-5 w-5" />}
              onClick={() => setShowCreateModal(true)}
            >
              New Request
            </Button>
          ) : undefined
        }
      />

      {/* One line by default -- search box + Filter toggle badged with the
          active count. The panel starts expanded whenever a filter is already
          set (restored from `?f_*` after a refresh), so a filtered list is
          never painted with nothing on screen to explain it. */}
      <CompactFilterBar
        search={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search requests..."
        activeFilterCount={activeFilterCount}
        onClearFilters={clearFilters}
      >
        <Select
          id="servicing-filter-status"
          label="Status"
          options={statusOptions}
          value={status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
        />
        <Select
          id="servicing-filter-type"
          label="Type"
          options={typeOptions}
          value={requestType}
          onChange={(e) => handleFilterChange('request_type', e.target.value)}
        />
        <Select
          id="servicing-filter-priority"
          label="Priority"
          options={priorityOptions}
          value={priority}
          onChange={(e) => handleFilterChange('priority', e.target.value)}
        />
      </CompactFilterBar>

      {/* A REFRESH that failed while the rows it was replacing are still in
          hand -- react-query keeps the previous page's data on a refetch of the
          same key, so without this the list would look current while the
          server had stopped answering. */}
      {isError && requests.length > 0 && (
        <div
          role="alert"
          className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300"
        >
          <span>
            Could not refresh this list. These are the results from before your last change.
          </span>
          <button type="button" className="font-medium underline" onClick={() => void refetch()}>
            Try again
          </button>
        </div>
      )}

      <Card padding="none">
        {/* Three states, never two: a failed request, an empty queue and a
            loading list are different facts. This page used to have TWO -- a
            failed request rendered "No requests found", telling a broker there
            was nothing to see when the server had simply refused. */}
        {isLoading ? (
          <TableSkeleton />
        ) : isError && requests.length === 0 ? (
          <ErrorState onRetry={() => void refetch()} />
        ) : requests.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* U-02: a bounded, scrollable viewport so `position: sticky` on
                the header has a scroll container to stick against -- against an
                unbounded wrapper the PAGE scrolls and nothing ever sticks. */}
            <div className="max-h-[65vh] overflow-x-auto overflow-y-auto">
              {/* `w-full` next to `min-w-full` is what makes `table-fixed` do
                  anything at all -- see COLUMN_WIDTHS. */}
              <table className="w-full min-w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700">
                <colgroup>
                  {COLUMN_WIDTHS.map((width, index) => (
                    <col key={index} style={{ width }} />
                  ))}
                </colgroup>
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <Th>Request</Th>
                    <Th>Policy</Th>
                    <Th className={TIGHT}>Type</Th>
                    <Th className={TIGHT}>Priority</Th>
                    <Th className={TIGHT}>Status</Th>
                    {/* F7: without this column the only way to see who owns a
                        request was to open it one by one. The list serializer
                        has always sent `assigned_to_name`. */}
                    <Th>Assignee</Th>
                    {/* U-04: the ONE orderable column -- `created_at` is in
                        ServiceRequestViewSet.ordering_fields. */}
                    <Th
                      className={TIGHT}
                      sortKey="created_at"
                      activeSortKey={sortKey}
                      sortDirection={sortDirection}
                      onSortChange={handleSortChange}
                    >
                      Created
                    </Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {/* B2: the row already looked clickable (cursor-pointer,
                      hover) but did nothing -- there was no detail route at
                      all, so a raised request could never be opened. */}
                  {requests.map((request) => (
                    <tr
                      key={request.id}
                      role="link"
                      tabIndex={0}
                      aria-label={`Open servicing request ${request.request_number}`}
                      onClick={() => navigate(`/servicing-requests/${request.id}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/servicing-requests/${request.id}`);
                        }
                      }}
                      className="cursor-pointer transition-colors hover:bg-gray-50 focus:bg-gray-50 focus:outline-none dark:hover:bg-gray-700/50 dark:focus:bg-gray-700/50"
                    >
                      <Td>
                        <div>
                          <p
                            className="truncate text-sm font-medium text-gray-900 dark:text-white"
                            title={request.request_number}
                          >
                            {request.request_number}
                          </p>
                          <p
                            className="truncate text-sm text-gray-500 dark:text-gray-400"
                            title={request.subject}
                          >
                            {request.subject}
                          </p>
                        </div>
                      </Td>
                      <Td>
                        <p
                          className="truncate text-sm text-gray-900 dark:text-white"
                          title={policyNumberLabel(request)}
                        >
                          <PolicyNumber source={request} />
                        </p>
                        <span className="block truncate" title={request.customer_name}>
                          <EntityLink
                            kind="contact"
                            id={request.customer}
                            name={request.customer_name}
                            className="text-xs text-gray-500 hover:underline dark:text-gray-400"
                          />
                        </span>
                        {/* U-05: whose policy it is. The list had no insurer
                            at all; the logo sits beside the name, in this cell
                            rather than a column the width budget cannot fit. */}
                        {request.insurer_name && (
                          <span
                            className="mt-0.5 flex items-center gap-1.5"
                            title={request.insurer_name}
                          >
                            <InsurerLogo
                              src={request.insurer_logo_url}
                              name={request.insurer_name}
                              description={request.insurer_description}
                              size="xs"
                            />
                            <span className="min-w-0 truncate text-xs text-gray-500 dark:text-gray-400">
                              {request.insurer_name}
                            </span>
                          </span>
                        )}
                      </Td>
                      <Td className={TIGHT}>
                        {/* A Badge is inline-flex -- an atomic inline box that a
                            `truncate` ancestor CLIPS with no ellipsis. `max-w-full`
                            plus a `truncate` span inside is what lets it shrink. */}
                        <Badge variant="info" size="sm" className="max-w-full">
                          <span className="truncate" title={requestTypeLabel(request.request_type)}>
                            {requestTypeLabel(request.request_type)}
                          </span>
                        </Badge>
                      </Td>
                      <Td className={TIGHT}>
                        <Badge
                          variant={priorityBadgeVariant(request.priority)}
                          size="sm"
                          className="max-w-full"
                        >
                          <span className="truncate" title={priorityLabel(request.priority)}>
                            {priorityLabel(request.priority)}
                          </span>
                        </Badge>
                      </Td>
                      <Td className={TIGHT}>
                        <Badge
                          variant={requestStatusBadgeVariant(request.status)}
                          size="sm"
                          className="max-w-full"
                        >
                          <span className="truncate" title={statusLabel(request.status)}>
                            {statusLabel(request.status)}
                          </span>
                        </Badge>
                        {isRequestOverdue(request) && (
                          <Badge variant="error" size="sm" className="mt-1 max-w-full">
                            <span className="truncate">Overdue</span>
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        {/* Same fallback wording as the detail page so the two
                            screens read identically for an unowned request.
                            "Unassigned" was text-gray-400 on white (~2.8:1,
                            under the 4.5:1 floor for body text); gray-500
                            carries the same "nothing here" weight and passes. */}
                        <p
                          className={cn(
                            'truncate text-sm',
                            request.assigned_to_name
                              ? 'text-gray-900 dark:text-white'
                              : 'text-gray-500 dark:text-gray-400'
                          )}
                          title={request.assigned_to_name || 'Unassigned'}
                        >
                          {request.assigned_to_name || 'Unassigned'}
                        </p>
                      </Td>
                      <Td className={cn(TIGHT, 'text-sm text-gray-500 dark:text-gray-400')}>
                        <span className="block truncate" title={formatDateTime(request.created_at)}>
                          {formatDateTime(request.created_at)}
                        </span>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      {/* Create Request Modal */}
      <Modal isOpen={showCreateModal} onClose={closeCreateModal} title="New Servicing Request">
        <div className="space-y-4">
          {/* C5: was a free-text "Policy ID" box expecting a pasted UUID. */}
          <div>
            <label
              htmlFor="servicing-policy"
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Policy <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <SearchableSelect
              id="servicing-policy"
              value={newRequest.policy}
              onChange={(value) => {
                setSelectedPolicyOption(policyOptions.find((o) => o.value === value) ?? null);
                setNewRequest((prev) => ({ ...prev, policy: value ?? '' }));
                handlePolicyHolderPrefill(value ?? '');
              }}
              options={[{ value: '', label: 'Select a policy' }, ...policyOptions]}
              placeholder="Select a policy"
              // Server-driven mode: the options are exactly what the server
              // matched (number, holder, phone, ...), with no local re-filter.
              onQueryChange={setPolicyQuery}
              loading={policiesFetching}
            />
          </div>
          <Select
            label="Request Type"
            options={typeOptions.filter((t) => t.value)}
            value={newRequest.request_type}
            onChange={(e) => setNewRequest((prev) => ({ ...prev, request_type: e.target.value }))}
          />
          <Input
            label="Subject"
            value={newRequest.subject}
            onChange={(e) => setNewRequest((prev) => ({ ...prev, subject: e.target.value }))}
            placeholder="Brief description"
            required
          />
          <Select
            label="Priority"
            options={priorityOptions.filter((p) => p.value)}
            value={newRequest.priority}
            onChange={(e) => setNewRequest((prev) => ({ ...prev, priority: e.target.value }))}
          />
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Description <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <textarea
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:focus:ring-primary-400"
              rows={4}
              value={newRequest.description}
              onChange={(e) => setNewRequest((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Detailed description..."
            />
          </div>

          {/* C6: the three requester fields. Required by the model, so their
              absence was the C4 validation error. Prefilled from the logged-in
              user, editable for a request raised on someone else's behalf. */}
          <div className="border-t border-gray-200 pt-2 dark:border-gray-700">
            <p className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
              Requester details
            </p>
            <div className="space-y-4">
              <Input
                label="Requester Name"
                value={newRequest.requester_name}
                onChange={(e) => editRequester('requester_name', e.target.value)}
                placeholder="Full name"
                required
              />
              <Input
                label="Requester Email"
                type="email"
                value={newRequest.requester_email}
                onChange={(e) => editRequester('requester_email', e.target.value)}
                placeholder="name@example.com"
                required
              />
              <Input
                label="Requester Phone"
                inputMode="numeric"
                value={newRequest.requester_phone}
                onChange={(e) => editRequester('requester_phone', e.target.value)}
                placeholder="10-digit mobile number"
                required
              />
            </div>
          </div>
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={closeCreateModal}>
            Cancel
          </Button>
          <Button onClick={handleCreate} isLoading={createMutation.isPending} disabled={!canSubmit}>
            Create Request
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

/**
 * A header cell.
 *
 * `sortKey` opts the column into U-04. Deliberately opt-IN: this project has
 * already shipped the opposite (every header looked clickable, most did
 * nothing), and the fix was to make sortability something a column has to ask
 * for and the server has to support.
 *
 * The label sits in a `<span>` inside the `<button>` because `text-overflow`
 * applies to a block container's inline content, never to an atomic
 * inline-level box -- a `truncate` on the `<th>` around the button would hard
 * CLIP the label with no ellipsis. The sort state is announced once, by
 * `aria-sort` on the `<th>`; the arrow is `aria-hidden` so the button's
 * accessible name does not change as the user toggles it.
 */
function Th({
  children,
  className,
  sortKey,
  activeSortKey,
  sortDirection,
  onSortChange,
}: {
  children: string;
  className?: string;
  sortKey?: string;
  activeSortKey?: string;
  sortDirection?: SortDirection;
  onSortChange?: (key: string, direction: SortDirection) => void;
}) {
  const isSortable = Boolean(sortKey && onSortChange);
  const isActive = isSortable && activeSortKey === sortKey;
  const direction: SortDirection = sortDirection ?? 'asc';
  return (
    <th
      title={children}
      aria-sort={
        isSortable
          ? isActive
            ? direction === 'asc'
              ? 'ascending'
              : 'descending'
            : 'none'
          : undefined
      }
      className={cn(
        'truncate px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300',
        // An opaque background on the sticky cell itself: the thead's dark
        // background is translucent, so rows would show through it.
        'sticky top-0 z-10 bg-gray-50 dark:bg-gray-700',
        className
      )}
    >
      {isSortable ? (
        <button
          type="button"
          onClick={() => onSortChange!(sortKey!, isActive && direction === 'asc' ? 'desc' : 'asc')}
          className="group inline-flex max-w-full items-center gap-1 rounded uppercase tracking-wider hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:text-gray-100"
        >
          <span className="truncate">{children}</span>
          {isActive && (
            <svg
              aria-hidden="true"
              focusable="false"
              data-testid="sort-indicator"
              data-direction={direction}
              className="h-3 w-3 shrink-0 text-gray-500 dark:text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d={direction === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'}
              />
            </svg>
          )}
        </button>
      ) : (
        children
      )}
    </th>
  );
}

/** A body cell. `truncate` (overflow:hidden) is what `table-fixed` needs to
 *  keep a long value inside its declared width instead of blowing the column
 *  out; the content inside carries its own `title` so a clipped value can
 *  still be read on hover. */
function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('truncate px-6 py-4', className)}>{children}</td>;
}

function TableSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-12 bg-gray-100 dark:bg-gray-700" />
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="flex h-16 items-center gap-4 border-t border-gray-100 px-6 dark:border-gray-700"
        >
          <div className="flex-1">
            <div className="mb-2 h-4 w-24 rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-3 w-40 rounded bg-gray-200 dark:bg-gray-700" />
          </div>
          <div className="h-4 w-28 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-6 w-20 rounded-full bg-gray-200 dark:bg-gray-700" />
          <div className="h-6 w-16 rounded-full bg-gray-200 dark:bg-gray-700" />
          <div className="h-6 w-20 rounded-full bg-gray-200 dark:bg-gray-700" />
          {/* F7: one placeholder per column -- Assignee, then Created. */}
          <div className="h-4 w-24 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-4 w-28 rounded bg-gray-200 dark:bg-gray-700" />
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="py-12 text-center">
      <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-white">
        No requests found
      </h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        No servicing requests match your search criteria.
      </p>
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="py-12 text-center" role="alert">
      <h3 className="mt-4 text-sm font-semibold text-red-700 dark:text-red-400">
        Couldn&apos;t load servicing requests.
      </h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        That is not the same as there being none — please try again.
      </p>
      <Button variant="outline" className="mt-4" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}
